// §1 — Loan Snapshot (embedded variant only; the standalone hero from the
// prototype isn't used inside the Loan Center tab). Read-only Encompass context.
import { EncFlag, Icon, ROField } from '../atoms';
import { useDocOrderCtx } from '../context';
import { fmtDate, fmtMoney } from '../format';
import { lockExpiredAtClosing } from '../core';
import type { SnapshotExtras } from '../viewModel';

interface SnapshotProps {
  extras: SnapshotExtras;
  closingDate: string | null;
  stepNo: number;
}

export function SnapshotSection({ extras, closingDate, stepNo }: SnapshotProps) {
  const { loanFacts: loan } = useDocOrderCtx();
  const reservesShort = (loan.reservesFile !== null && loan.reservesFile !== undefined) && (loan.reservesLock !== null && loan.reservesLock !== undefined) && Number(loan.reservesFile) < Number(loan.reservesLock);
  const lockExpired = lockExpiredAtClosing(loan.lockExpiration, closingDate);
  const isGovt = /\b(fha|va)\b/i.test(loan.product || '');
  const pct = (v: number | null) => ((v !== null && v !== undefined) ? `${v}%` : '—');
  const reserves =
    (loan.reservesLock === null || loan.reservesLock === undefined) && (loan.reservesFile === null || loan.reservesFile === undefined)
      ? '—'
      : `${(loan.reservesLock !== null && loan.reservesLock !== undefined) ? `${loan.reservesLock} mo` : '—'} / ${(loan.reservesFile !== null && loan.reservesFile !== undefined) ? `${loan.reservesFile} mo` : '—'}${
          reservesShort ? ` · Short ${Number(loan.reservesLock) - Number(loan.reservesFile)} mo` : ''
        }`;

  return (
    <section className="ws-section" id="sec-snapshot">
      <div className="card card-pad snapshot-compact">
        <div className="ws-section-head">
          <h3 className="ws-section-title">
            <span className="ws-step-no">{stepNo}</span>Loan Snapshot
          </h3>
          <EncFlag label="Synced from Encompass" />
        </div>
        <div className="snap-borrowers">
          <Icon name="users" size={14} className="snap-borrowers-ico" />
          <span className="snap-borrowers-lbl">{loan.borrowers.length > 1 ? `${loan.borrowers.length} Borrowers` : 'Borrower'}</span>
          <div className="snap-borrowers-list">
            {loan.borrowers.map((b, i) => (
              <span key={i} className="snap-borrower">
                {b}
              </span>
            ))}
          </div>
        </div>
        <div className="field-grid cols-3 compact">
          <ROField label="Property" value={loan.property} span={3} />
          {!isGovt ? (
            <ROField label="Loan Amount" value={fmtMoney(loan.loanAmount)} encField={{ id: 1109, desc: 'Total Loan Amount' }} />
          ) : (
            <ROField
              label="Base / Total Loan Amount"
              value={`${fmtMoney((loan.baseLoanAmount !== null && loan.baseLoanAmount !== undefined) ? loan.baseLoanAmount : loan.loanAmount)} / ${fmtMoney(loan.loanAmount)}`}
              encField={{ id: 1109, desc: 'Base Loan Amount (Field 1109) / Total Loan Amount (Field 2 — incl. MIP / funding fee)' }}
            />
          )}
          <ROField label="Note Rate" value={pct(extras.rate)} />
          <ROField label="Product" value={loan.product} />
          <ROField label="Purpose" value={loan.purpose} />
          <ROField label="LTV / CLTV" value={`${pct(extras.ltv)} / ${pct(extras.cltv)}`} />
          {/* R4 #15: a DSCR program (Field 1401) prices on the debt-service
              ratio, not the borrower's DTI — swap the row. Displayed as a
              RATIO ("1.25x"), never a percent. */}
          {/dscr/i.test(loan.loanProgram1401 || '') ? (
            <ROField
              label="DSCR Ratio"
              value={extras.dscrRatio !== null ? `${extras.dscrRatio.toFixed(2)}x` : '\u2014'}
            />
          ) : (
            <ROField label="DTI" value={`${pct(extras.housingRatio)} / ${pct(extras.dtiRatio)}`} />
          )}
          <ROField label="Impounds / Escrows" value={extras.impoundsWaived} />
          <ROField label="Occupancy" value={extras.occupancy} />
          <ROField label="Property Type" value={loan.propertyType} />
          <ROField label="Lien Position" value={extras.lienPosition} />
          <ROField
            label="Lock Expiration"
            value={fmtDate(loan.lockExpiration)}
            warn={lockExpired}
            warnTip="Closing date falls after the lock expiration"
          />
          <ROField
            label="Reserves (Locked vs Documented)"
            value={reserves}
            warn={reservesShort}
            warnTip="Documented reserves are below locked reserves"
          />
        </div>
      </div>
    </section>
  );
}
